import React, { useState, useEffect, useCallback, useMemo } from 'react'
import PerfectScrollbar from 'react-perfect-scrollbar'
import {
  Card,
  CardContent,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Grid,
  TextField,
  Button,
  Chip,
} from '@material-ui/core'
import DeleteBtn from 'components/DeleteBtn'
import { checkPermission, getPermissionCodeByPath } from 'helpers/permission'
import {
  permissionModuleListPromise,
  permissionModuleRoutesPromise,
  permissionModuleCreatePromise,
  permissionModuleUpdatePromise,
  permissionModuleDeletePromise,
} from 'api/PermissionModule'

// The permission catalogue, joined against the routes the API registered at boot. A route with no
// code is not locked down -- adminPermissionCheck lets it through for every logged-in admin, which
// is why the gaps count is the headline and not a footnote.
const PAGE_SIZE = 25

export default function PermissionModuleList () {
  const [routes, setRoutes] = useState({ routes: [], total: 0, gaps: 0 })
  const [modules, setModules] = useState([])
  const [keyword, setKeyword] = useState('')
  const [onlyGaps, setOnlyGaps] = useState(false)
  const [draft, setDraft] = useState({})
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState(null)
  const [page, setPage] = useState(0)
  const [orphanPage, setOrphanPage] = useState(0)

  const canWrite = checkPermission(getPermissionCodeByPath(window.location.pathname, ['edit', 'upsert', 'add']))

  const load = useCallback(async () => {
    const [routeData, moduleData] = await Promise.all([
      permissionModuleRoutesPromise(),
      permissionModuleListPromise(),
    ])
    setRoutes(routeData || { routes: [], total: 0, gaps: 0 })
    setModules(moduleData || [])
  }, [])

  useEffect(() => { load() }, [load])

  const moduleByName = useMemo(() => {
    const map = {}
    modules.forEach(module => { map[module.name] = module })
    return map
  }, [modules])

  const rows = useMemo(() => (routes.routes || []).filter(route => {
    if (onlyGaps && route.code) return false
    if (!keyword) return true
    const needle = keyword.toLowerCase()
    return route.name.toLowerCase().includes(needle) ||
      (route.path || '').toLowerCase().includes(needle) ||
      (route.code || '').includes(needle)
  }), [routes.routes, keyword, onlyGaps])

  // Both endpoints return their whole table on purpose: the gaps count and the orphan table below
  // are computed by comparing the two complete sets, so paginating either one server-side would
  // lose them. 326 routes and 169 module rows is a rendering cost, not a transfer one, so the
  // paging lives here.
  const pageRows = useMemo(() => rows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE), [rows, page])

  const orphans = useMemo(
    () => modules.filter(module => !(routes.routes || []).some(route => route.name === module.name)),
    [modules, routes.routes]
  )
  const orphanRows = useMemo(
    () => orphans.slice(orphanPage * PAGE_SIZE, (orphanPage + 1) * PAGE_SIZE),
    [orphans, orphanPage]
  )

  // A filter that shortens the list must not leave the viewer on a page that no longer exists.
  useEffect(() => { setPage(0) }, [keyword, onlyGaps])

  async function handleSave (route) {
    const code = (draft[route.name] || '').trim()
    if (!code) return

    setBusy(true)
    try {
      const existing = moduleByName[route.name]
      if (existing) await permissionModuleUpdatePromise(existing.id, { code })
      else await permissionModuleCreatePromise({ name: route.name, code })
      setDraft(current => ({ ...current, [route.name]: '' }))
      await load()
      setNotice(null)
    } catch (err) {
      setNotice(`${route.name}: ${err.message}`)
    } finally {
      setBusy(false)
    }
  }

  async function handleDelete (route) {
    const existing = moduleByName[route.name]
    if (!existing) return
    if (!window.confirm(`Remove the code from ${route.name}? That does not lock the route down -- it opens it to every logged-in admin.`)) return

    setBusy(true)
    try {
      const resp = await permissionModuleDeletePromise(existing.id)
      await load()
      setNotice((resp && resp.warning) || null)
    } catch (err) {
      setNotice(`${route.name}: ${err.message}`)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={'root'}>
      <div className={'content'}>
        <Card>
          <CardHeader
            title='Admin permission catalogue'
            subheader={`${routes.total} registered admin routes, ${routes.gaps} with no permission code. A route with no code is reachable by every logged-in admin -- closing these is what the fail-closed permission check is waiting on.`}
          />
          <CardContent>
            <Grid container item xs={12} spacing={2}>
              <Grid item xs={5}>
                <TextField
                  fullWidth
                  variant='outlined'
                  size='small'
                  placeholder='Route key, path or code'
                  value={keyword}
                  className='background-color'
                  onChange={event => setKeyword(event.target.value)}
                />
              </Grid>
              <Grid item xs={7}>
                <Button variant={onlyGaps ? 'contained' : 'outlined'} color='primary' onClick={() => setOnlyGaps(current => !current)}>
                  {onlyGaps ? `Showing the ${routes.gaps} gaps` : 'Show only routes with no code'}
                </Button>
              </Grid>
            </Grid>

            { notice && <p className='tips' style={{ color: '#b00020' }}>{notice}</p> }

            <br />

            <PerfectScrollbar>
              <div className={'inner'}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Route key</TableCell>
                      <TableCell>Methods</TableCell>
                      <TableCell>Path</TableCell>
                      <TableCell>Audit module</TableCell>
                      <TableCell>Code</TableCell>
                      <TableCell>Action</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {pageRows.map(route => (
                      <TableRow key={route.name} hover>
                        <TableCell>{route.name}</TableCell>
                        <TableCell>{(route.methods || []).join(', ')}</TableCell>
                        <TableCell>{route.path}</TableCell>
                        <TableCell>{route.adminModule || '-'}</TableCell>
                        <TableCell>
                          { route.code
                            ? <Chip size='small' label={route.code} />
                            : <Chip size='small' color='secondary' label='open to every admin' /> }
                        </TableCell>
                        <TableCell className='flex-left'>
                          <TextField
                            variant='outlined'
                            size='small'
                            placeholder={route.code || 'code'}
                            value={draft[route.name] || ''}
                            onChange={event => { const { value } = event.target; setDraft(current => ({ ...current, [route.name]: value })) }}
                          />
                          &nbsp;
                          { canWrite && <Button size='small' color='primary' disabled={busy} onClick={() => handleSave(route)}>Save</Button> }
                          { route.code && <DeleteBtn onClick={() => handleDelete(route)} /> }
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </PerfectScrollbar>

            <TablePagination
              component='div'
              rowsPerPage={PAGE_SIZE}
              page={page}
              count={rows.length}
              rowsPerPageOptions={[PAGE_SIZE]}
              onPageChange={(event, next) => setPage(next)}
            />
          </CardContent>
        </Card>

        <br />

        <Card>
          <CardHeader
            title='Codes with no registered route'
            subheader='Rows whose route key no longer exists in the API. They gate nothing; they are what is left when a route is renamed or deleted.'
          />
          <CardContent>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Code</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orphanRows.map(module => (
                  <TableRow key={module.id} hover>
                    <TableCell>{module.id}</TableCell>
                    <TableCell>{module.name}</TableCell>
                    <TableCell>{module.code}</TableCell>
                    <TableCell>
                      <DeleteBtn onClick={() => handleDelete({ name: module.name })} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <TablePagination
              component='div'
              rowsPerPage={PAGE_SIZE}
              page={orphanPage}
              count={orphans.length}
              rowsPerPageOptions={[PAGE_SIZE]}
              onPageChange={(event, next) => setOrphanPage(next)}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
